/**
 * Building3D
 *
 * Individual 3D building component for the campus overview.
 * Renders a stylized building based on LocationConfig with hover/selection states.
 * Uses BuildingModel for distinct per-building geometry.
 */

import React, { useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { CampusLocation, LocationConfig } from '../../../types/spatial';
import { Lock } from 'lucide-react';
import { BuildingModel } from './buildingModels';

export interface Building3DProps {
  config: LocationConfig;
  isUnlocked: boolean;
  isSelected: boolean;
  isHovered: boolean;
  onClick: (location: CampusLocation) => void;
  onHover: (location: CampusLocation | null) => void;
}

// Bounding dimensions for label/ring positioning
const BUILDING_DIMENSIONS: Record<CampusLocation, [number, number, number]> = {
  [CampusLocation.CAMPUS_OVERVIEW]: [1, 1, 1],
  [CampusLocation.HEADQUARTERS]: [10, 12, 10],
  [CampusLocation.DESIGN_STUDIO]: [7, 8, 6],
  [CampusLocation.RD_LAB]: [8, 7, 7],
  [CampusLocation.MANUFACTURING]: [12, 6, 9],
  [CampusLocation.SALES_OFFICE]: [6, 7, 6],
  [CampusLocation.EXECUTIVE_OFFICE]: [8, 17, 8],
  [CampusLocation.COMPANY_ARCHIVES]: [6, 5, 6],
};

export const Building3D: React.FC<Building3DProps> = ({
  config,
  isUnlocked,
  isSelected,
  isHovered,
  onClick,
  onHover,
}) => {
  const groupRef = useRef<THREE.Group>(null);
  const [localHover, setLocalHover] = useState(false);

  const dimensions = BUILDING_DIMENSIONS[config.id] || [5, 5, 5];
  const color = isUnlocked ? config.color : config.lockedColor;

  // Animate: hover scale for unlocked, gentle bob for locked
  useFrame(({ clock }) => {
    if (groupRef.current) {
      const targetScale = (isHovered || localHover) && isUnlocked ? 1.05 : 1;
      groupRef.current.scale.lerp(
        new THREE.Vector3(targetScale, targetScale, targetScale),
        0.1
      );

      // Locked buildings: subtle vertical float to suggest "waiting to be unlocked"
      if (!isUnlocked) {
        groupRef.current.position.y = Math.sin(clock.getElapsedTime() * 0.8) * 0.15;
      } else {
        groupRef.current.position.y = 0;
      }
    }
  });

  const handlePointerOver = (e: THREE.Event) => {
    e.stopPropagation();
    setLocalHover(true);
    onHover(config.id);
    document.body.style.cursor = 'pointer';
  };

  const handlePointerOut = (e: THREE.Event) => {
    e.stopPropagation();
    setLocalHover(false);
    onHover(null);
    document.body.style.cursor = 'auto';
  };

  const handleClick = (e: THREE.Event) => {
    e.stopPropagation();
    onClick(config.id);
  };

  return (
    <group
      position={[config.position.x, config.position.y + dimensions[1] / 2, config.position.z]}
      rotation={[0, config.rotation, 0]}
    >
      {/* Interactive building group with scale animation */}
      <group
        ref={groupRef}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onClick={handleClick}
      >
        <BuildingModel type={config.id} color={color} />
      </group>

      {/* Building label */}
      <Html
        position={[0, dimensions[1] / 2 + 2, 0]}
        center
        distanceFactor={12}
        style={{
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        <div className="flex flex-col items-center gap-1">
          <div
            className="px-3 py-1.5 rounded-lg text-white font-semibold whitespace-nowrap shadow-lg
              bg-gray-900/80 backdrop-blur-sm border border-white/10 font-display"
            style={{ fontSize: '14px' }}
          >
            {config.buildingName}
          </div>
          {!isUnlocked && (
            <div className="flex items-center gap-1 px-2 py-1 bg-gray-900/80 backdrop-blur-sm
              border border-white/10 rounded text-xs text-white/50">
              <Lock className="w-3 h-3" />
              <span>Locked</span>
            </div>
          )}
        </div>
      </Html>

      {/* Selection indicator ring */}
      {isSelected && isUnlocked && (
        <mesh position={[0, -dimensions[1] / 2 + 0.1, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[Math.max(...dimensions) * 0.6, Math.max(...dimensions) * 0.7, 32]} />
          <meshBasicMaterial color={config.color} transparent opacity={0.5} />
        </mesh>
      )}
    </group>
  );
};

export default Building3D;
